import { useEditor, EditorContent } from "@tiptap/react";
import type { Editor } from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import { useEffect } from "react";
import {
  Bold, Italic, Strikethrough, Underline as UnderlineIcon,
  Heading2, Heading3, List, ListOrdered, Link2, RemoveFormatting,
} from "lucide-react";
import DOMPurify from "dompurify";

interface Props {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  minHeight?: string;
}

/**
 * Rich text editor used in form-builder fields whose content is shown to end users.
 * Outputs HTML. Render with DOMPurify.sanitize() on public pages.
 */
export function RichTextEditor({ value, onChange, placeholder, minHeight = "120px" }: Props) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { target: "_blank", rel: "noopener noreferrer nofollow" },
      }),
    ],
    content: value || "",
    editorProps: {
      attributes: {
        class:
          "prose prose-sm max-w-none focus:outline-none px-3 py-2 text-sm text-foreground " +
          "[&_a]:text-primary [&_a]:underline",
        style: `min-height: ${minHeight}`,
        "data-placeholder": placeholder ?? "",
      },
    },
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      // Treat empty editor (<p></p>) as ""
      onChange(html === "<p></p>" ? "" : html);
    },
  });

  // Keep external value in sync (e.g., after load)
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const incoming = value || "";
    if (incoming !== current && incoming !== (current === "<p></p>" ? "" : current)) {
      editor.commands.setContent(incoming || "", { emitUpdate: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  if (!editor) return null;

  return (
    <div className="rounded-md border border-input bg-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-0">
      <Toolbar editor={editor} />
      <EditorContent editor={editor} />
    </div>
  );
}

function Toolbar({ editor }: { editor: Editor }) {
  const chain = () => editor.chain().focus() as any;
  const Btn = ({
    onClick, active, disabled, title, children,
  }: {
    onClick: () => void;
    active?: boolean;
    disabled?: boolean;
    title: string;
    children: React.ReactNode;
  }) => (
    <button
      type="button"
      title={title}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      disabled={disabled}
      className={
        "p-1.5 rounded text-foreground hover:bg-accent disabled:opacity-40 " +
        (active ? "bg-accent text-accent-foreground" : "")
      }
    >
      {children}
    </button>
  );

  const setLink = () => {
    const prev = editor.getAttributes("link").href as string | undefined;
    const url = window.prompt("Link URL (leave empty to remove)", prev ?? "https://");
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  return (
    <div className="flex flex-wrap items-center gap-0.5 px-1 py-1 border-b border-border bg-muted/30 sticky top-0 z-10 rounded-t-md">
      <Btn title="Bold (⌘B)" onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")}>
        <Bold className="w-3.5 h-3.5" />
      </Btn>
      <Btn title="Italic (⌘I)" onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")}>
        <Italic className="w-3.5 h-3.5" />
      </Btn>
      <Btn title="Strike" onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive("strike")}>
        <Strikethrough className="w-3.5 h-3.5" />
      </Btn>
      <span className="w-px h-4 bg-border mx-1" />
      <Btn title="Heading 2" onClick={() => chain().toggleHeading({ level: 2 }).run()} active={editor.isActive("heading", { level: 2 })}>
        <Heading2 className="w-3.5 h-3.5" />
      </Btn>
      <Btn title="Heading 3" onClick={() => chain().toggleHeading({ level: 3 }).run()} active={editor.isActive("heading", { level: 3 })}>
        <Heading3 className="w-3.5 h-3.5" />
      </Btn>
      <span className="w-px h-4 bg-border mx-1" />
      <Btn title="Bullet list" onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")}>
        <List className="w-3.5 h-3.5" />
      </Btn>
      <Btn title="Numbered list" onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")}>
        <ListOrdered className="w-3.5 h-3.5" />
      </Btn>
      <span className="w-px h-4 bg-border mx-1" />
      <Btn title="Link" onClick={setLink} active={editor.isActive("link")}>
        <Link2 className="w-3.5 h-3.5" />
      </Btn>
      <Btn title="Clear formatting" onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}>
        <RemoveFormatting className="w-3.5 h-3.5" />
      </Btn>
    </div>
  );
}

/**
 * Convenience render component for public pages.
 * Sanitizes HTML and applies prose styling.
 */
export function RichTextDisplay({ html, className = "" }: { html: string | null | undefined; className?: string }) {
  if (!html) return null;
  return (
    <div
      className={`prose prose-sm max-w-none ${className}`}
      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html) }}
    />
  );
}
